import { formatData } from '@utilities';

import { buildExportItemKey } from './export_items';
import {
  getBarChartData,
  getBarTornadoChartData,
  getColumnChartData,
  getColumnSplineChartData,
  getScatterRegressionLineChartData,
  getSplineChartData,
} from './chart_helpers';
import { getTableData } from './table_helpers';

import { zipObject } from 'lodash';

/**
 * builds a { key: numbroFormat } dict from the comma/semicolon-delimited axis attributes
 * @param  {string} keys    e.g. "spend,trips"
 * @param  {string} formats e.g. "0.0%;0,0"
 * @return {Object}
 */
const buildDict = (keys, formats) =>
  keys && formats ? zipObject(keys.split(','), formats.split(';')) : {};

// Object-shaped chart types (data points are objects, not plain numbers) that have no native
// PptxGenJS equivalent — exported as a rasterized image of the live Highcharts instance.
const IMAGE_FALLBACK_CHART_TYPES = new Set(['bullet', 'scatter', 'waterfall']);

/**
 * resolves the active chart target keys (respecting a column filter override)
 * @param  {Object} activeColumnFilter
 * @param  {string} axis comma-delimited axis attribute
 * @return {Array}
 */
const resolveChartTarget = ({ activeColumnFilter, axis }) =>
  (activeColumnFilter ? activeColumnFilter?.value?.chartKey : axis?.split(',')) || [];

// Highcharts helpers apply `limit` to series values but historically leave the full category array.
// When every series has the same limited length, apply that same intentional limit to categories.
// Other mismatches remain untouched so canonical validation can reject the tab instead of losing data.
const alignCategoriesToSeries = ({ series = [], xAxis }) => {
  const lengths = [...new Set(series.map(({ data = [] }) => data.length))];
  if (lengths.length !== 1 || !Array.isArray(xAxis?.categories)) return xAxis;
  const [seriesLength] = lengths;
  return xAxis.categories.length > seriesLength
    ? { ...xAxis, categories: xAxis.categories.slice(0, seriesLength) }
    : xAxis;
};

/**
 * Maps a waterfall dataset to a native stacked-bar IR (PptxGenJS has no native waterfall type). Each
 * category is stacked as [base, increase, decrease, total]; a category is non-zero in exactly one
 * VISIBLE series, so — with the base series colored to blend with the slide — the bar lands at the
 * right height with the right color, as an editable native bar chart.
 *
 * @param  {Object} dataSet     the resolved data set ({ data, name })
 * @param  {boolean} isSum      layout isSum flag (last point is a cumulative total bar)
 * @param  {string} title       slide title (dataset name)
 * @param  {string} xaxis       category field
 * @param  {string} yaxis       value field
 * @param  {string} yAxisFormat numbro format for the value axis
 * @return {Object} { type:'waterfall', title, categories, series:[{name,values}], format }
 */
export const mapWaterfallToExportItem = ({ dataSet, isSum, title, xaxis, yaxis, yAxisFormat }) => {
  const rows = dataSet?.data || [];
  const categories = [];
  const labels = [];
  const base = [];
  const increase = [];
  const decrease = [];
  const total = [];
  let cumulative = 0;

  rows.forEach((row, index) => {
    const raw = row?.[yaxis];
    const value = raw === 'NAN_SENTINEL' || raw == null ? 0 : Number(raw);
    categories.push(row?.[xaxis]);
    labels.push(formatData({ format: yAxisFormat, value }));

    const isTotalBar = index === 0 || (isSum && index === rows.length - 1);
    if (isTotalBar) {
      base.push(0);
      increase.push(0);
      decrease.push(0);
      total.push(value);
      cumulative = value;
    } else if (value >= 0) {
      base.push(cumulative);
      increase.push(value);
      decrease.push(0);
      total.push(0);
      cumulative += value;
    } else {
      base.push(cumulative + value);
      increase.push(0);
      decrease.push(Math.abs(value));
      total.push(0);
      cumulative += value;
    }
  });

  return {
    categories,
    format: yAxisFormat,
    labels,
    series: [
      { name: 'base', values: base },
      { name: 'Increase', values: increase },
      { name: 'Decrease', values: decrease },
      { name: 'Total', values: total },
    ],
    title,
    type: 'waterfall',
  };
};

const buildWaterfallExportItem = ({
  activeColumnFilter,
  dataSet,
  index = 0,
  isSum,
  orientation,
  tabId,
  title,
  type,
  xaxis,
  yaxis,
  yAxisFormat,
}) => {
  if (!dataSet?.data?.length) return null;

  // Waterfall layouts use a two-key chartKey: [numeric value, category label]. The raw xaxis/yaxis
  // attributes contain every metric's keys (for example "spend,units"), so reading them directly
  // produces undefined values. Resolve the selected metric exactly as getWaterfallChartData does.
  const [filteredValueKey, filteredCategoryKey] = activeColumnFilter?.value?.chartKey || [];
  const valueKey = filteredValueKey || yaxis;
  const categoryKey = filteredCategoryKey || xaxis;
  const format = buildDict(yaxis, yAxisFormat)?.[valueKey] || yAxisFormat;

  return {
    ...mapWaterfallToExportItem({
      dataSet,
      isSum,
      title: title || dataSet.name,
      xaxis: categoryKey,
      yaxis: valueKey,
      yAxisFormat: format,
    }),
    key: buildExportItemKey({
      orientation,
      tabId,
      type,
      variant: activeColumnFilter?.value?.chartKey?.join(',') || dataSet.id,
      xaxis: categoryKey,
      yaxis: valueKey,
    }),
    order: index * 10,
    tabId,
  };
};

/**
 * Maps a Report <Chart>'s parsed attributes + data to the PPTX export IR consumed by the PowerPoint
 * slide renderers, or null when the chart type isn't wired for export yet.
 *
 * This is the single place that (a) selects the right getXxxChartData transform, (b) lifts the numbro
 * format token out of yAxisDict — the getXxxChartData outputs only expose it via Highcharts formatter
 * closures — and (c) tags the IR with the renderer key. Adding a native chart type is one case here
 * plus one entry in the exporter's renderer registry (see DownloadPowerPoint CHART_SLIDE_RENDERERS).
 *
 * @param  {Object} activeColumnFilter
 * @param  {Array}  filteredData
 * @param  {string} limit
 * @param  {string} orientation 'v' | 'h'
 * @param  {string} type        <Chart> type attribute
 * @param  {string} xaxis
 * @param  {string} xAxisTickAngle
 * @param  {string} yaxis
 * @param  {string} yAxisFormat
 * @return {Object|null} { type, series, xAxis, format } or null
 */
export const mapToExportIR = ({
  activeColumnFilter,
  filteredData,
  groupBy,
  limit,
  orientation,
  type,
  xaxis,
  xAxisFormat,
  xAxisTickAngle,
  xAxisTitle,
  xMax,
  xMin,
  yaxis,
  yAxisFormat,
  yAxisTitle,
  yMax,
  yMin,
}) => {
  if (!filteredData?.length) return null;

  const yAxisDict = buildDict(yaxis, yAxisFormat);

  if (type === 'scatter+line') {
    const data = getScatterRegressionLineChartData({
      filteredData,
      grouping: groupBy,
      xaxis,
      xAxisTitle,
      xMax: xMax ?? 1,
      xMin: xMin ?? 0,
      yaxis,
      yAxisDict,
      yAxisTitle,
      yMax: yMax ?? 1,
      yMin: yMin ?? 0,
    });
    const [, ...pointSeries] = data.series || [];
    return {
      format: yAxisDict[yaxis] || yAxisFormat,
      series: pointSeries.map((series) => ({
        data: series.data.map((point) => ({
          name: point.name,
          x: Number(point.x),
          y: Number(point.y),
        })),
        name: series.name,
      })),
      showRegressionLine: true,
      type: 'Scatter Chart',
      xAxisTitle,
      xFormat: buildDict(xaxis, xAxisFormat)[xaxis] || xAxisFormat,
      xMax: Number(xMax ?? 1),
      xMin: Number(xMin ?? 0),
      yAxisTitle,
      yMax: Number(yMax ?? 1),
      yMin: Number(yMin ?? 0),
    };
  }

  if (type === 'tornado') {
    const xAxisDict = buildDict(xaxis, xAxisFormat);
    const data = getBarTornadoChartData({
      activeColumnFilter,
      filteredData,
      limit,
      xaxis,
      xAxisDict,
      yaxis,
    });
    const [target] = resolveChartTarget({ activeColumnFilter, axis: xaxis });
    return {
      format: xAxisDict[target],
      series: data?.series,
      type: 'Tornado Chart',
      xAxis: alignCategoriesToSeries({ series: data?.series, xAxis: data?.xAxis?.[0] }),
    };
  }

  // vertical bar -> native column (getColumnChartData already returns a top-level `format`)
  if (orientation === 'v' && type === 'bar') {
    const data = getColumnChartData({
      activeColumnFilter,
      filteredData,
      limit,
      xaxis,
      xRotation: xAxisTickAngle,
      yaxis,
      yAxisDict,
    });
    return {
      format: data?.format,
      series: data?.series,
      type: 'Bar Chart',
      xAxis: alignCategoriesToSeries({ series: data?.series, xAxis: data?.xAxis }),
    };
  }

  // horizontal bar -> native bar
  if (orientation === 'h' && type === 'bar') {
    const xAxisDict = buildDict(xaxis, xAxisFormat);
    const data = getBarChartData({
      activeColumnFilter,
      filteredData,
      limit,
      xaxis,
      xAxisDict,
      yaxis,
    });
    const [target] = resolveChartTarget({ activeColumnFilter, axis: xaxis });
    return {
      barDir: 'bar',
      format: xAxisDict?.[target],
      series: data?.series,
      type: 'Bar Chart',
      xAxis: alignCategoriesToSeries({ series: data?.series, xAxis: data?.xAxis }),
    };
  }

  // spline -> native line
  if (type === 'spline') {
    const data = getSplineChartData({
      activeColumnFilter,
      filteredData,
      limit,
      xaxis,
      yaxis,
      yAxisDict,
    });
    const [target] = resolveChartTarget({ activeColumnFilter, axis: yaxis });
    return {
      format: yAxisDict?.[target],
      series: data?.series,
      type: 'Line Chart',
      xAxis: alignCategoriesToSeries({ series: data?.series, xAxis: data?.xAxis }),
    };
  }

  // column+spline (Pareto) -> native combo (bar + line), with a per-series format array
  if (type === 'column+spline') {
    const data = getColumnSplineChartData({
      activeColumnFilter,
      filteredData,
      limit,
      xaxis,
      xRotation: xAxisTickAngle,
      yaxis,
      yAxisDict,
    });
    const targets = resolveChartTarget({ activeColumnFilter, axis: yaxis });
    return {
      format: targets.map((key) => yAxisDict?.[key]),
      series: data?.series,
      type: 'Column Spline Chart',
      xAxis: alignCategoriesToSeries({ series: data?.series, xAxis: data?.xAxis }),
    };
  }

  return null;
};

/**
 * Expands a Report <Chart> into the export items it should contribute to downloadData.items[].
 *
 * A single chart backed by a Metric-style column filter renders one chart per option on screen (only
 * the selected one is visible), so it must export one slide per option — not just the selection. When
 * there is exactly one column filter we iterate its options (synthesizing the activeColumnFilter each
 * option would produce) and title each item by the option label. Otherwise we emit a single item for
 * the current selection.
 *
 * @param  {Object} activeColumnFilter the currently selected filter (used for the single-item path)
 * @param  {Array}  columnFilters      data.filters.columns (each { label, options:[{ label, value }] })
 * @param  {Object} chartAttrs         the <Chart> attributes + filteredData (see mapToExportIR)
 * @param  {string|number} tabId
 * @return {Array} export items ({ ...ir, key, tabId, title? })
 */
export const mapChartToExportItems = ({
  activeColumnFilter,
  columnFilters,
  defaultDataId,
  filteredData,
  groupBy,
  isSum,
  limit,
  orientation,
  tabId,
  type,
  xaxis,
  xAxisFormat,
  xAxisTickAngle,
  xAxisTitle,
  xMax,
  xMin,
  yaxis,
  yAxisFormat,
  yAxisTitle,
  yMax,
  yMin,
}) => {
  const baseAttrs = {
    filteredData,
    groupBy,
    limit,
    orientation,
    type,
    xaxis,
    xAxisFormat,
    xAxisTickAngle,
    xAxisTitle,
    xMax,
    xMin,
    yaxis,
    yAxisFormat,
    yAxisTitle,
    yMax,
    yMin,
  };

  // A chart pinned to a dataset (defaultDataId) only renders when that dataset is in the active
  // (group-filtered) data — mirror that here so we never register a chart that isn't on screen.
  const isRendered =
    defaultDataId === undefined || filteredData?.some((set) => set?.id === defaultDataId);
  if (!isRendered) return [];

  const [metricFilter, ...otherFilters] = columnFilters || [];

  // one export item per Metric option (each option is its own chart). `order` interleaves each
  // chart with its matching table (see mapTableToExportItems) so the deck reads metric-by-metric.
  if (metricFilter?.options?.length && otherFilters.length === 0) {
    const items = metricFilter.options.reduce((acc, option, index) => {
      const optionFilter = { value: option.value };
      const ir = mapToExportIR({ ...baseAttrs, activeColumnFilter: optionFilter });
      if (ir) {
        acc.push({
          ...ir,
          key: buildExportItemKey({
            orientation,
            tabId,
            type,
            variant: option?.value?.chartKey?.join(','),
            xaxis,
            yaxis,
          }),
          order: index * 10,
          tabId,
          title: option.label,
        });
      } else if (type === 'waterfall') {
        const dataSet = defaultDataId
          ? filteredData?.find((set) => set?.id === defaultDataId)
          : filteredData?.[0];
        const waterfall = buildWaterfallExportItem({
          activeColumnFilter: optionFilter,
          dataSet,
          index,
          isSum,
          orientation,
          tabId,
          title: option.label,
          type,
          xaxis,
          yaxis,
          yAxisFormat,
        });
        if (waterfall) acc.push(waterfall);
      }
      return acc;
    }, []);
    if (items.length) return items;
  }

  // single native item for the current selection
  const ir = mapToExportIR({ ...baseAttrs, activeColumnFilter });
  if (ir) {
    return [
      {
        ...ir,
        key: buildExportItemKey({ orientation, tabId, type, variant: defaultDataId, xaxis, yaxis }),
        order: 0,
        tabId,
      },
    ];
  }

  // native waterfall (stacked-bar emulation) from the resolved dataset
  if (type === 'waterfall') {
    const dataSet = defaultDataId
      ? filteredData?.find((set) => set?.id === defaultDataId)
      : filteredData?.[0];
    const waterfall = buildWaterfallExportItem({
      activeColumnFilter,
      dataSet,
      isSum,
      orientation,
      tabId,
      type,
      xaxis,
      yaxis,
      yAxisFormat,
    });
    if (waterfall) return [waterfall];
  }

  // image fallback for object-shaped chart types with no native mapping. The item is a marker; the
  // caller attaches the live Highcharts ref, which createImageSlide rasterizes via getSVG() at export.
  // `variant: defaultDataId` keeps two same-type charts (e.g. dual waterfalls) as distinct items.
  if (filteredData?.length && IMAGE_FALLBACK_CHART_TYPES.has(type)) {
    return [
      {
        key: buildExportItemKey({ orientation, tabId, type, variant: defaultDataId, xaxis, yaxis }),
        order: 0,
        tabId,
        type: 'image',
      },
    ];
  }

  return [];
};

/**
 * Expands a Report <Table> into tab-scoped export items. A table backed by a Metric column filter
 * renders different columns per option (via the option's `target`), so — like the chart — it exports
 * one table slide per option, each with an `order` (option index * 10 + 1) so it sits right after its
 * matching chart. A plain table exports a single item. Everything is stamped with tabId so the
 * exporter never leaks a table from another tab (there is no non-tab-scoped fallback).
 *
 * @param  {Object} activeColumnFilter current selection (used for the plain single-table case)
 * @param  {Array}  columnFilters      data.filters.columns
 * @param  {Node}   children           the <Column> children of the <Table>
 * @param  {string} defaultDataId
 * @param  {Array}  filteredData
 * @param  {string|number} tabId
 * @return {Array} table export items ({ type:'table', table:{columns,data}, title?, key, tabId, order })
 */
export const mapTableToExportItems = ({
  activeColumnFilter,
  children,
  columnFilters,
  defaultDataId,
  filteredData,
  tabId,
}) => {
  const [metricFilter, ...otherFilters] = columnFilters || [];

  // one table per Metric option (columns differ per option via `target`)
  if (metricFilter?.options?.length && otherFilters.length === 0) {
    const items = metricFilter.options.reduce((acc, option, index) => {
      const { columns, rows } = getTableData({
        activeColumnFilter: { value: option.value },
        children,
        defaultDataId,
        filteredData,
      });
      if (columns?.length && rows?.length) {
        acc.push({
          key: buildExportItemKey({
            tabId,
            type: 'table',
            variant: option?.value?.chartKey?.join(','),
          }),
          order: index * 10 + 1,
          sourceDataId: defaultDataId,
          tabId,
          table: { columns, data: rows },
          title: option.label,
          type: 'table',
        });
      }
      return acc;
    }, []);
    if (items.length) return items;
  }

  // plain table for the current selection (tab-scoped; keyed by dataset so dual tables stay distinct)
  const { columns, rows } = getTableData({
    activeColumnFilter,
    children,
    defaultDataId,
    filteredData,
  });
  if (!columns?.length || !rows?.length) return [];

  return [
    {
      key: buildExportItemKey({ tabId, type: 'table', variant: defaultDataId }),
      order: 1,
      sourceDataId: defaultDataId,
      tabId,
      table: { columns, data: rows },
      type: 'table',
    },
  ];
};
